/**
 * Hook for attorney profile page: profile resolution, matters list, lastActive.
 * TODO(backend): Replace MOCK_ATTORNEYS lookup with attorney query by attorneyId.
 */
import { useParams } from 'react-router-dom';
import { MOCK_ATTORNEYS } from '~/shared/fixtures';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import { matterHasActiveExpertParticipation } from '~/modules/matters/matterCardUtils';
import { useMattersQuery } from '~/modules/matters/useMattersQuery';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import type { User } from '~/shared/types/connect';
import { UserRole } from '~/shared/types/connect';

export interface UseAttorneyProfilePageReturn {
  attorney: User | null;
  isOwnProfile: boolean;
  mattersForList: MatterViewModel[];
  lastActive: string | null;
  isLoading: boolean;
  error: Error | undefined;
}

export function useAttorneyProfilePage(): UseAttorneyProfilePageReturn {
  const { attorneyId } = useParams();
  const { user: currentUser } = useCurrentUser();
  const { matters, isLoading, error } = useMattersQuery();

  const mattersInput = error ? [] : matters;

  const isOwnProfile =
    currentUser?.role === UserRole.ATTORNEY &&
    Boolean(attorneyId && attorneyId === currentUser.id);

  let attorney: User | null = null;
  if (isOwnProfile && currentUser) {
    attorney = currentUser;
  } else if (attorneyId) {
    attorney =
      MOCK_ATTORNEYS.find((candidate) => candidate.id === attorneyId) ?? null;
  }

  let mattersForList: MatterViewModel[] = [];
  if (isOwnProfile) {
    mattersForList = mattersInput.filter(
      (matter) => matter.attorneyId === currentUser?.id,
    );
  } else if (attorney) {
    mattersForList = mattersInput.filter(
      (matter) =>
        matterHasActiveExpertParticipation(matter, currentUser?.id ?? '') &&
        matter.attorneyId === attorney!.id,
    );
  }

  const lastActive =
    mattersForList.length > 0
      ? mattersForList.reduce(
          (newest, matter) =>
            (matter.updatedAt ?? matter.createdAt) > newest
              ? matter.updatedAt ?? matter.createdAt
              : newest,
          mattersForList[0].updatedAt ?? mattersForList[0].createdAt,
        ) || null
      : null;

  return {
    attorney,
    isOwnProfile,
    mattersForList,
    lastActive,
    isLoading,
    error,
  };
}
